// karta wpisu blog - wariant `overlay` (gradient) lub `extended` (jak karta produktu + meta)

import React, { FC } from 'react';
import classnames from 'classnames';
import { format } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';
import { CalendarEvent, ChevronRight } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';

import type { IHomeBlogArticleItem } from 'api/endpoints/useGetHomeBlogArticles';
import { Link } from 'components/controls';
import WiseLabel from 'components/wise/WiseLabel';

import styles from 'theme/components/wise/WiseBlogCard/WiseBlogCard.module.scss';

const NEWS_LABEL = { type: 'PROMOTION' as const, name: 'Aktualności' };
const TUTORIAL_LABEL = { type: 'PROMOTION' as const, name: 'Baza wiedzy' };
const POPULAR_LABEL = { type: 'PROMOTION' as const, name: 'Popularne' };

const SUBTITLE_MAX_CHARS = 150;

/**
 * Usuwa tagi HTML, dekoduje encje (&ndash;, &nbsp;, &oacute;, &amp; ...) i ucina
 * tekst do `maxChars` znaków, dokładając wielokropek `…` na końcu jeśli był skrócony.
 */
const stripAndTruncateHtml = (raw: string | undefined, maxChars: number): string => {
  if (!raw) return '';
  // Zamień <br>/</p> na spację żeby nie kleić słów po usunięciu tagów.
  const withBreaks = raw.replace(/<\s*br\s*\/?\s*>/gi, ' ').replace(/<\/\s*p\s*>/gi, ' ');
  const withoutTags = withBreaks.replace(/<[^>]+>/g, '');
  // Dekodowanie encji HTML - wykorzystujemy parser przeglądarki.
  let decoded = withoutTags;
  if (typeof document !== 'undefined') {
    const el = document.createElement('textarea');
    el.innerHTML = withoutTags;
    decoded = el.value;
  }
  const collapsed = decoded.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= maxChars) return collapsed;
  return collapsed.slice(0, maxChars).trimEnd() + '…';
};

export type WiseBlogCardOverlayProps = {
  variant?: 'overlay';
  title: string;
  backgroundPicture?: string | null;
  to: string;
};

export type WiseBlogCardExtendedProps = {
  variant: 'extended';
  article: IHomeBlogArticleItem;
};

export type WiseBlogCardProps = WiseBlogCardOverlayProps | WiseBlogCardExtendedProps;

const isExtended = (p: WiseBlogCardProps): p is WiseBlogCardExtendedProps => p.variant === 'extended';

const WiseBlogCard: FC<WiseBlogCardProps> = (props) => {
  const { i18n } = useTranslation();

  if (isExtended(props)) {
    const { article } = props;
    const articleTo = `/blog/${article.url_key}`;
    const dateLocale = i18n.language?.toLowerCase().startsWith('pl') ? pl : enUS;
    const todayLabel = format(new Date(), 'd MMMM yyyy', { locale: dateLocale });

    return (
      <article
        className={classnames(
          styles.rootExtended,
          'StylePath-Components-wise-WiseBlogCard-WiseBlogCardExtended'
        )}>
        <div className={styles.imageBlockExt}>
          <Link to={articleTo} className={styles.imageLinkExt} aria-label={article.title}>
            {article.background_picture ? (
              <img
                className={styles.imageExt}
                src={article.background_picture}
                alt=""
                loading="lazy"
              />
            ) : (
              <div className={styles.imagePlaceholderExt} aria-hidden />
            )}
          </Link>
          <div className={styles.overlayTopExt}>
            <div className={styles.labelsExt}>
              {article.is_news && <WiseLabel label={NEWS_LABEL} />}
              {article.is_popular && <WiseLabel label={POPULAR_LABEL} />}
            </div>
          </div>
        </div>
        <div className={styles.bodyExt}>
          <Link to={articleTo} className={styles.titleLinkExt}>
            <h3 className={styles.articleTitleExt}>{article.title}</h3>
          </Link>
          {article.subtitle ? (
            <p className={styles.subtitleExt}>
              {stripAndTruncateHtml(article.subtitle, SUBTITLE_MAX_CHARS)}
            </p>
          ) : null}
          <div className={styles.metaRowExt}>
            <span className={styles.dateBlockExt}>
              <CalendarEvent className={styles.calendarIconExt} aria-hidden />
              <span className={styles.dateTextExt}>{todayLabel}</span>
            </span>
            <Link to={articleTo} className={styles.readMoreBtnExt}>
              <Trans>Czytaj całość</Trans>
            </Link>
          </div>
        </div>
      </article>
    );
  }

  const { title, backgroundPicture, to } = props;

  return (
    <Link
      to={to}
      className={classnames(styles.root, 'StylePath-Components-wise-WiseBlogCard')}
      aria-label={title}>
      {backgroundPicture ? (
        <img className={styles.image} src={backgroundPicture} alt="" loading="lazy" />
      ) : null}
      <div className={styles.gradient} aria-hidden />
      <div className={styles.bottom}>
        <p className={styles.title}>{title}</p>
        <span className={styles.navCue} aria-hidden>
          <ChevronRight />
        </span>
      </div>
    </Link>
  );
};

export default WiseBlogCard;
